import { Answers, Config, Drug, Step } from "../types";
import { ReactNode, useState } from "react";
import { calculateResults } from "../utils";
import InputWithIcon from "../../../components/InputWithIcon";
import { FaWeightScale } from "react-icons/fa6";

const TRIMESTER_LABELS: Record<string, string> = {
	"trimester 1": "שליש ראשון",
	"trimester 2": "שליש שני",
	"trimester 3": "שליש שלישי",
};

const DIALYSIS_LABELS: Record<string, string> = {
	hemodialysis: "המודיאליזה",
	capd: "CAPD",
	crrt: "CRRT",
};

type RenalRuleMatch = {
	doseText: string | null;
	source: "hemodialysis" | "capd" | "crrt" | "cct" | "none";
};

function getRenalContextLabel(s: Answers["sensitivities"]): string | null {
	if (!s?.kidneyIssue) return null;

	if (s.onDialysis && s.dialysisType) {
		return DIALYSIS_LABELS[s.dialysisType] ?? s.dialysisType;
	}

	if (!s.onDialysis && s.cctValue != null) {
		return `CCT/CrCl ${s.cctValue}`;
	}

	return "תפקוד כלייתי";
}

function getRenalDoseText(
	drugInfo: Drug | undefined,
	answers: Answers
): RenalRuleMatch {
	const s = answers.sensitivities;

	if (!drugInfo || !s?.kidneyIssue) {
		return { doseText: null, source: "none" };
	}

	const renal = drugInfo.renalAdjustmentAdult;
	if (!renal) {
		return { doseText: null, source: "none" };
	}

	// Dialysis path must win over everything else
	if (s.onDialysis === true) {
		if (s.dialysisType === "hemodialysis") {
			return {
				doseText: renal.hemodialysis ?? null,
				source: "hemodialysis",
			};
		}

		if (s.dialysisType === "capd") {
			return {
				doseText: renal.capd ?? null,
				source: "capd",
			};
		}

		if (s.dialysisType === "crrt") {
			return {
				doseText: renal.crrt ?? null,
				source: "crrt",
			};
		}

		return { doseText: null, source: "none" };
	}

	// Non-dialysis renal adjustment by CCT / CrCl
	if (s.cctValue == null) {
		return { doseText: null, source: "none" };
	}

	const matchedRule = renal.cct?.find((rule: any) => {
		const minOk =
			rule?.predicate?.minCCT == null || s.cctValue >= rule.predicate.minCCT;
		const maxOk =
			rule?.predicate?.maxCCT == null || s.cctValue <= rule.predicate.maxCCT;

		return minOk && maxOk;
	});

	if (!matchedRule) {
		return { doseText: null, source: "none" };
	}

	return {
		doseText: matchedRule?.details?.overrideMode ?? null,
		source: matchedRule?.details?.overrideMode ? "cct" : "none",
	};
}

function getDisplayedDosage(
	drug: any,
	drugInfo: Drug | undefined,
	answers: Answers,
	parsedWeight: number,
	weightIsValid: boolean
): { dosageText: string | null; isRenalAdjusted: boolean } {
	const renalMatch = getRenalDoseText(drugInfo, answers);

	// If renal dose exists, always use it first
	if (renalMatch.doseText) {
		return {
			dosageText: renalMatch.doseText,
			isRenalAdjusted: true,
		};
	}

	// Otherwise fallback to per-kg / regular dose
	if (drug.isPerKg) {
		const unit = drug.unit || "mg";

		if (weightIsValid) {
			const raw = drug.dosePerKg * parsedWeight;
			const capped = drug.maxDose ? Math.min(raw, drug.maxDose) : raw;
			const rounded = Math.round(capped * 10) / 10;
			const wasCapped = drug.maxDose && raw > drug.maxDose;

			return {
				dosageText: wasCapped
					? `${rounded}${unit} (max ${drug.maxDose}${unit})`
					: `${rounded}${unit} (${drug.dosePerKg}${unit}/kg × ${parsedWeight}kg)`,
				isRenalAdjusted: false,
			};
		}

		return {
			dosageText: `${drug.dosePerKg}${unit}${
				drug.maxDose ? `/kg (max ${drug.maxDose}${unit})` : "/kg"
			}`,
			isRenalAdjusted: false,
		};
	}

	return {
		dosageText: drug.dosage || null,
		isRenalAdjusted: false,
	};
}

export function Summary({
	answers,
	onBack,
	handleReset,
	config,
	categories,
	drugs,
	progressBar,
	customTitle,
}: {
	answers: Answers;
	onBack: () => void;
	handleReset: () => void;
	config: Config;
	categories: { id: string; name: string }[];
	drugs: Drug[];
	steps?: Step[];
	progressBar?: ReactNode;
	customTitle?: string;
}) {
	const results = calculateResults(answers, config, drugs);
	const [weightKg, setWeightKg] = useState<string>("");

	const parsedWeight = parseFloat(weightKg);
	const weightIsValid = !isNaN(parsedWeight) && parsedWeight > 0;

	const hasNoDrugs =
		results.firstLineOptions.length === 0 && results.alternativeOptions.length === 0;

	const hasPerKgDrugs = [...results.firstLineOptions, ...results.alternativeOptions].some(
		(opt: any) => opt.drugs.some((d: any) => d.isPerKg)
	);

	const renderTreatmentOption = (option: any, optionIndex: number, optionsArray: any[]) => {
		const isAllowed = option.isAllowed;

		return (
			<div
				key={optionIndex}
				className={`treatment-option-link ${isAllowed ? "allowed" : "removed"}`}
				onClick={(e) => e.preventDefault()}
			>
				<div className="treatment-link-content">
					{(optionsArray.length > 1 || !isAllowed) && (
						<div className="treatment-link-header">
							{optionsArray.length > 1 && (
								<span className="treatment-type">אפשרות {optionIndex + 1}</span>
							)}
							{!isAllowed && <span className="treatment-status">✗ אסור</span>}
						</div>
					)}

					{option.drugs.map((drug: any, drugIdx: number) => {
						const drugInfo = drugs.find((d: any) => d.id === drug.drugId);
						const name = drugInfo?.displayName || drug.drugId;

						const { dosageText, isRenalAdjusted } = getDisplayedDosage(
							drug,
							drugInfo,
							answers,
							parsedWeight,
							weightIsValid
						);

						const renalLabel = getRenalContextLabel(answers.sensitivities);

						const mainParts = [
							dosageText,
							drug.frequency && !isRenalAdjusted ? `X ${drug.frequency}` : null,
						].filter(Boolean);

						const mainLine = mainParts.join(" ");
						const durationPart = !isRenalAdjusted && drug.duration ? `, ${drug.duration}` : "";
						const howToGivePart = !isRenalAdjusted && drug.howToGive ? ` (${drug.howToGive})` : "";

						return (
							<div key={drugIdx} className="drug-details">
								<div className="drug-main-line">
									<strong>{name}:</strong>{" "}
									<span dir="auto">
										{mainLine}
										{durationPart}
										{howToGivePart}
									</span>
								</div>

								{isRenalAdjusted && renalLabel && (
									<div className="drug-comment-line" dir="auto">
										מינון מותאם - {renalLabel}
									</div>
								)}

								{drug.comment && (
									<div className="drug-comment-line" dir="auto">
										{drug.comment}
									</div>
								)}
							</div>
						);
					})}

					{option.invalidDrugs && option.invalidDrugs.length > 0 && (
						<div className="invalid-drugs-details">
							<div className="invalid-drugs-title">תרופות לא מתאימות:</div>
							{option.invalidDrugs.map((drug: any, drugIndex: number) => {
								const drugInfo = drugs.find((d: any) => d.id === drug.drugId);
								const reason =
									drug.fullReason ||
									(drug.isBlacklisted
										? "תרופה ברשימה האסורה"
										: drug.isAllergic
											? "אלרגיה ידועה"
											: "");

								return (
									<div key={drugIndex} className="invalid-drug-item">
										<span className="drug-name">
											{drugInfo?.displayName || drug.drugId}
										</span>
										<span className="drug-reason"> - {reason}</span>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		);
	};

	const getProtocolText = () => {
		if (!answers.protocol) return null;
		const protocol = config.protocols?.find(
			(p: any) => String(p.id) === String(answers.protocol)
		);
		return protocol?.displayName ?? answers.protocol;
	};

	const getDiagnosisText = () => {
		if (!answers.diagnosis) return null;
		const protocol = config.protocols?.find(
			(p: any) => String(p.id) === String(answers.protocol)
		);
		const diagnosis = protocol?.diagnoses?.find(
			(d: any) => String(d.id) === String(answers.diagnosis)
		);
		return diagnosis?.displayName ?? answers.diagnosis;
	};

	const getSensitivitiesText = () => {
		if (!answers.sensitivities) return null;

		const s = answers.sensitivities;
		const parts: string[] = [];

		if (s.kidneyIssue) {
			if (s.onDialysis && s.dialysisType) {
				parts.push(`בעיה בתפקודי כליות - ${DIALYSIS_LABELS[s.dialysisType] ?? s.dialysisType}`);
			} else if (!s.onDialysis && s.cctValue != null) {
				parts.push(`בעיה בתפקודי כליות - CCT/CrCl ${s.cctValue}`);
			} else {
				parts.push("בעיה בתפקודי כליות");
			}
		}

		if (s.pregnant && s.trimester) {
			parts.push(`הריון - ${TRIMESTER_LABELS[s.trimester] ?? s.trimester}`);
		}

		if (s.nursing) {
			parts.push("הנקה");
		}

		if (s.hasAllergies && s.selectedAllergies?.length > 0) {
			const names = s.selectedAllergies.map(
				(id: string) => drugs.find((d) => d.id === id)?.displayName ?? id
			);
			parts.push(names.join(", "));
		}

		if (parts.length === 0) return null;
		return parts.join(", ");
	};

	const protocolText = getProtocolText();
	const diagnosisText = getDiagnosisText();
	const sensitivitiesText = getSensitivitiesText();

	return (
		<div className="antibiogram-container">
			<div className="antibiogram-header">
				<h2>{customTitle || 'מתן אנטיביוטיקה במלר"ד'}</h2>
			</div>

			{progressBar}

			<div className="antibiogram-content">
				<div className="step-content">
					<div className="step-header">
						<button className="back-step-button" onClick={onBack}>
							חזור →
						</button>
						<h3 className="step-title" style={customTitle === 'תצוגה מקדימה' ? { display: 'none' } : undefined}>המלצות</h3>
						<button className="back-step-button reset-button" onClick={handleReset}>
							התחל מחדש
						</button>
					</div>
				</div>
			</div>

			<div className="antibiogram-results">
				<div className="answers-plain-text">
					{protocolText && <div className="answer-line">{protocolText}</div>}
					{diagnosisText && (
						<div
							className="answer-line diagnosis-line"
							dangerouslySetInnerHTML={{ __html: diagnosisText }}
						/>
					)}
					{sensitivitiesText && (
						<div className="answer-line sensitivities-line">
							<strong>רגישויות: </strong>
							<span>{sensitivitiesText}</span>
						</div>
					)}
				</div>

				{hasPerKgDrugs && (
					<div className="weight-input-section">
						<InputWithIcon
							icon={<FaWeightScale size={20} color="#103C6E" />}
							placeholder='משקל מטופל (ק"ג)'
							value={weightKg}
							onChange={(e) => setWeightKg((e.target as HTMLInputElement).value)}
							type="number"
							inputMode="decimal"
							min="0"
							max="300"
							step="0.1"
						/>
					</div>
				)}

				{hasNoDrugs && (
					<div className="results-section no-drugs-alert">
						<p className="no-treatment-warning">
							⚠️ אין אפשרויות טיפול זמינות. כל האפשרויות הוסרו בגלל תרופות לא מתאימות
							(אלרגיות, הריון/הנקה). יש להתייעץ עם הרופא המטפל.
						</p>
					</div>
				)}

				{(results.firstLineOptions.length > 0 || results.alternativeOptions.length > 0) && (
					<>
						{results.firstLineOptions.length > 0 && (
							<div className="results-section treatment-options-section">
								<h3>קו ראשון - אפשרויות טיפול:</h3>
								<div className="treatment-options-list">
									{results.firstLineOptions.map(renderTreatmentOption)}
								</div>
							</div>
						)}

						{results.alternativeOptions.length > 0 && (
							<div className="results-section treatment-options-section">
								<h3>חלופות - אפשרויות טיפול:</h3>
								<div className="treatment-options-list">
									{results.alternativeOptions.map(renderTreatmentOption)}
								</div>
							</div>
						)}
					</>
				)}

				{(results.protocolComment ||
					results.diagnosisComment ||
					(results.diagnosisRecommendations?.length ?? 0) > 0 ||
					(results.diagnosisSpecialCases?.length ?? 0) > 0 ||
					results.recommendations.length > 0) && (
					<div className="results-section recommendations-section">
						<h3>המלצות והערות</h3>
						<ul>
							{results.protocolComment && <li>{results.protocolComment}</li>}
							{results.diagnosisComment && <li>{results.diagnosisComment}</li>}

							{results.diagnosisRecommendations &&
								results.diagnosisRecommendations.length > 0 &&
								(results.diagnosisRecommendations.length === 1 ? (
									<li>{results.diagnosisRecommendations[0]}</li>
								) : (
									<li>
										בדיקות אבחנתיות:
										<ul>
											{results.diagnosisRecommendations.map((rec, i) => (
												<li key={i}>{rec}</li>
											))}
										</ul>
									</li>
								))}

							{results.diagnosisSpecialCases &&
								results.diagnosisSpecialCases.length > 0 &&
								(results.diagnosisSpecialCases.length === 1 ? (
									<li>מקרה מיוחד: {results.diagnosisSpecialCases[0]}</li>
								) : (
									<li>
										מקרים מיוחדים:
										<ul>
											{results.diagnosisSpecialCases.map((sc, i) => (
												<li key={i}>{sc}</li>
											))}
										</ul>
									</li>
								))}

							{results.recommendations &&
								results.recommendations.length > 0 &&
								results.recommendations.map((rec, index) => (
									<li key={`rec-${index}`}>{rec}</li>
								))}
						</ul>
					</div>
				)}
			</div>
		</div>
	);
}